import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import AdBlock from "@/components/AdBlock";
import { getPosts } from "@/lib/api";
import { categoryStyle } from "@/lib/constants";
import { Phone, Globe, Building } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function FeaturedPage() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        getPosts("featured").then(setPosts).catch(() => setPosts([]));
    }, []);

    return (
        <div data-testid="featured-page">
            <div className="px-4 md:px-8 lg:px-12 mt-10">
                <h1 className="section-title text-center mb-10">Featured</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 px-4 md:px-8 lg:px-12">
                <div className="lg:col-span-8 space-y-20">
                    {posts.map((post) => (
                        <article
                            key={post.id}
                            className="relative"
                            data-testid={`featured-article-${post.slug}`}
                        >
                            <span
                                className="badge-category absolute -top-2 left-0"
                                style={categoryStyle(post.category)}
                            >
                                {post.category}
                            </span>
                            <div className="pt-12">
                                <p className="font-sofia text-[#1B1B1B]/70 text-sm">
                                    {formatDate(post.date)}
                                </p>
                                <h2 className="font-poppins font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-2 text-[#1B1B1B] leading-tight">
                                    <Link
                                        to={`/post/${post.slug}`}
                                        className="hover:text-[#E63B26] transition"
                                        data-testid={`featured-article-title-${post.slug}`}
                                    >
                                        {post.title}
                                    </Link>
                                </h2>
                                {post.sub_header && (
                                    <p className="mt-3 font-sofia italic text-lg text-[#1B1B1B]/80">
                                        {post.sub_header}
                                    </p>
                                )}

                                <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6">
                                    <div className="md:col-span-8">
                                        <Link
                                            to={`/post/${post.slug}`}
                                            className="post-card block relative overflow-hidden aspect-[3/2] bg-[#1B1B1B]"
                                        >
                                            <img
                                                src={post.image_url}
                                                alt={post.title}
                                                className="post-card-image absolute inset-0 w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </Link>
                                    </div>
                                    <div className="md:col-span-4 space-y-3">
                                        {(post.thumbnails || [])
                                            .slice(0, 3)
                                            .map((t, i) => (
                                                <div
                                                    key={i}
                                                    className="aspect-square bg-[#1B1B1B] overflow-hidden"
                                                >
                                                    <img
                                                        src={t}
                                                        alt=""
                                                        className="w-full h-full object-cover hover:scale-105 transition duration-500"
                                                    />
                                                </div>
                                            ))}
                                        {(!post.thumbnails ||
                                            post.thumbnails.length === 0) && (
                                            <div className="aspect-square bg-[#A89489]/30 flex items-center justify-center font-poppins text-xs text-[#1B1B1B]/60 tracking-[0.2em] uppercase">
                                                Sample Photo
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Contact card */}
                                <div className="mt-6 font-sofia text-sm leading-relaxed">
                                    <p className="font-bold text-[#1B1B1B]">
                                        {post.contact_name}
                                    </p>
                                    <p className="text-[#1B1B1B]/80">
                                        <Building className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                                        {post.business}
                                    </p>
                                    <p className="text-[#1B1B1B]/80">
                                        <Phone className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                                        {post.phone}
                                    </p>
                                    <p className="text-[#E63B26]">
                                        <Globe className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                                        {post.website}
                                    </p>
                                </div>

                                <p className="mt-6 font-sofia text-[#1B1B1B]/85 leading-relaxed whitespace-pre-line">
                                    {post.body}
                                </p>

                                <div className="mt-6">
                                    <Link
                                        to={`/post/${post.slug}`}
                                        className="font-poppins text-sm tracking-[0.18em] uppercase text-[#E63B26] hover:text-[#1B1B1B] transition"
                                        data-testid={`featured-read-more-${post.slug}`}
                                    >
                                        Continue reading →
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="lg:col-span-4">
                    <Sidebar variant="with-ads" />
                </div>
            </div>
        </div>
    );
}
